import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Loader2, Sparkles, ChevronLeft, ChevronRight, Moon, Voicemail, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../../../App';
import ClickToCallButton from '../../../components/ClickToCallButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const fmtDur = (s) => {
  if (s == null) return '—';
  s = Math.round(s);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};
const fmtDt = (v) => v ? new Date(v).toLocaleString('it-IT', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome',
}) : '—';
const localDateParam = (date) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

const STATUS_BADGE = {
  answered: 'bg-emerald-100 text-emerald-700',
  missed: 'bg-rose-100 text-rose-700',
  abandoned: 'bg-slate-200 text-slate-600',
  other: 'bg-slate-100 text-slate-500',
};
const STATUS_LABEL = { answered: 'Risposta', missed: 'Persa', abandoned: 'Abbandonata', other: 'Altro' };

export function QualityDialog({ call, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!call) return;
    let stop = false;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/cs-control/calls/${call.id}/quality`, { headers: authHeaders() });
        if (stop) return;
        if (r.data.pending || r.data.error) {
          if (r.data.error) { setData(r.data); setLoading(false); return; }
          if (++tries < 24) setTimeout(poll, 5000);
          else { setLoading(false); toast.error('Timeout trascrizione'); }
        } else {
          setData(r.data);
          setLoading(false);
        }
      } catch (_) { setLoading(false); }
    };
    poll();
    return () => { stop = true; };
  }, [call]);

  const s = data?.scores || {};
  return (
    <Dialog open={!!call} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cs-quality-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" /> Analisi AI della chiamata
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Trascrizione e valutazione in corso (30-60s)...
          </div>
        ) : !data ? (
          <p className="text-sm text-slate-500 py-6">Nessuna analisi disponibile.</p>
        ) : data.error ? (
          <p className="text-sm text-rose-600 py-4">Errore: {data.error}</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-slate-100">{data.operator_name || 'Operatore ?'}</span>
              <span className="px-2 py-1 rounded bg-slate-100">{fmtDt(data.started_at)}</span>
              {s.motivo_chiamata && <span className="px-2 py-1 rounded bg-indigo-50 text-indigo-700">📋 {s.motivo_chiamata}</span>}
              {s.criticita && <span className="px-2 py-1 rounded bg-rose-100 text-rose-700 font-semibold">🚩 Criticità</span>}
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[['Cortesia', s.cortesia], ['Professionalità', s.professionalita], ['Risoluzione', s.risoluzione], ['Totale', s.punteggio]].map(([l, v]) => (
                <div key={l} className={`rounded-lg p-2 ${l === 'Totale' ? 'bg-indigo-600 text-white' : 'bg-slate-50'}`}>
                  <p className="text-[10px] uppercase opacity-70">{l}</p>
                  <p className="text-xl font-bold">{v ?? '—'}<span className="text-xs opacity-60">/10</span></p>
                </div>
              ))}
            </div>
            {s.commento && (
              <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">💬 {s.commento}</p>
            )}
            <details className="text-sm">
              <summary className="cursor-pointer font-medium text-slate-600">📝 Trascrizione completa</summary>
              <p className="mt-2 text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-3 max-h-64 overflow-y-auto">{data.transcript || '—'}</p>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CsCallsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [days, setDays] = useState(7);
  const [vmOnly, setVmOnly] = useState(false);
  const [oohOnly, setOohOnly] = useState(false);
  const [q, setQ] = useState('');
  const [qualityCall, setQualityCall] = useState(null);
  const size = 25;

  const load = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const from = localDateParam(new Date(Date.now() - (days - 1) * 864e5));
      const params = { date_from: from, page: p, size };
      if (status) params.status = status;
      if (vmOnly) params.voicemail_only = true;
      if (oohOnly) params.out_of_hours_only = true;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/cs-control/calls`, { headers: authHeaders(), params });
      setCalls(r.data.calls || []);
      setTotal(r.data.total || 0);
      setPage(p);
    } catch (e) {
      toast.error('Errore caricamento chiamate');
    }
    setLoading(false);
  }, [days, status, vmOnly, oohOnly, q]);

  useEffect(() => { load(0); }, [days, status, vmOnly, oohOnly]); // eslint-disable-line

  const startTranscribe = async (call) => {
    try {
      await axios.post(`${API}/cs-control/calls/${call.id}/transcribe`, {}, { headers: authHeaders() });
      setQualityCall(call);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore avvio trascrizione');
    }
  };

  const pages = Math.ceil(total / size);
  return (
    <div className="space-y-3" data-testid="cs-calls-tab">
      <div className="flex flex-wrap items-center gap-2">
        {[7, 14, 30].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`cs-calls-days-${d}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${days === d ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
            {d} giorni
          </button>
        ))}
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="cs-calls-status-filter"
          className="px-3 py-1.5 border rounded-lg text-sm bg-white">
          <option value="">Tutti gli stati</option>
          <option value="answered">Risposte</option>
          <option value="missed">Perse</option>
          <option value="abandoned">Abbandonate</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={vmOnly} onChange={e => setVmOnly(e.target.checked)} data-testid="cs-calls-vm-filter" /> Solo voicemail
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={oohOnly} onChange={e => setOohOnly(e.target.checked)} data-testid="cs-calls-ooh-filter" /> Solo fuori orario
        </label>
        <div className="flex items-center gap-1 ml-auto">
          <Input placeholder="Cerca numero..." value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(0)} className="w-44 h-8" data-testid="cs-calls-search" />
          <Button size="sm" variant="outline" onClick={() => load(0)} data-testid="cs-calls-search-button">Cerca</Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Numero / Ospite</th>
                  <th className="text-left px-3 py-2">Operatore</th>
                  <th className="text-left px-3 py-2">Stato</th>
                  <th className="text-right px-3 py-2">Attesa</th>
                  <th className="text-right px-3 py-2">Durata</th>
                  <th className="text-left px-3 py-2">Registrazione</th>
                  <th className="text-center px-3 py-2">AI</th>
                </tr>
              </thead>
              <tbody>
                {calls.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Nessuna chiamata trovata</td></tr>
                )}
                {calls.map(c => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cs-call-row-${c.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        {c.direction === 'outbound'
                          ? <ArrowUpRight className="h-3.5 w-3.5 text-sky-500" />
                          : <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-500" />}
                        {fmtDt(c.started_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-700 flex items-center gap-1.5">
                        {c.direction === 'outbound' ? c.to_number : c.from_number}
                        <ClickToCallButton number={c.direction === 'outbound' ? c.to_number : c.from_number} />
                      </p>
                      {c.guest && (
                        <p className="text-xs text-indigo-600" data-testid={`cs-call-guest-${c.id}`}>
                          👤 {c.guest.guest_name} {c.guest.property_name ? `· ${c.guest.property_name}` : ''}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{c.answerer_name || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[c.status] || STATUS_BADGE.other}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                      <span className="inline-flex gap-1 ml-1">
                        {c.out_of_hours && <Moon className="h-3.5 w-3.5 text-violet-500" title="Fuori orario" />}
                        {c.voicemail && <Voicemail className="h-3.5 w-3.5 text-amber-500" title="Voicemail" />}
                        {c.language === 'Inglese' && <span className="text-[10px]" title="Linea inglese">🇬🇧</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">{c.status === 'answered' ? fmtDur(c.ring_seconds) : fmtDur(c.duration_seconds)}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{fmtDur(c.talk_seconds)}</td>
                    <td className="px-3 py-2">
                      {c.recording_url || c.voicemail_url ? (
                        <audio controls preload="none" className="h-8 w-44" src={c.recording_url || c.voicemail_url} data-testid={`cs-call-audio-${c.id}`} />
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {c.quality_scored ? (
                        <button onClick={() => setQualityCall(c)} data-testid={`cs-call-score-${c.id}`}
                          className={`text-xs font-bold px-2 py-1 rounded-full ${(c.quality_score ?? 0) >= 7 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {c.quality_score ?? '?'} /10
                        </button>
                      ) : (c.recording_url || c.voicemail_url) && isAdmin ? (
                        <Button size="sm" variant="ghost" onClick={() => startTranscribe(c)} data-testid={`cs-call-transcribe-${c.id}`}
                          className="text-indigo-600 h-7 px-2">
                          <Sparkles className="h-3.5 w-3.5 mr-1" /> Trascrivi
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 text-sm text-slate-500">
          <span>{total} chiamate</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 0} onClick={() => load(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span>{page + 1} / {Math.max(pages, 1)}</span>
            <Button size="sm" variant="outline" disabled={page >= pages - 1} onClick={() => load(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {qualityCall && <QualityDialog call={qualityCall} onClose={() => { setQualityCall(null); load(page); }} />}
    </div>
  );
}
